import React, {useEffect, useMemo, useRef, useState} from "react";
import {
    Breadcrumb,
    Button,
    DatePicker,
    Divider,
    Empty,
    Input,
    Layout,
    Row,
    Select,
    Space,
    Table,
    Tag,
    Typography,
    theme
} from "antd";
import dayjs from "dayjs";
import {Link, useSearchParams} from "react-router-dom";
import Api from "../api";

const {Title} = Typography;
const {Content} = Layout;
const {RangePicker} = DatePicker;
const {useToken} = theme;

const BASE_URL = '/admin/v2/cancel-retention/discounts';

const DEFAULT_PER_PAGE = 20;
const DEFAULT_SORT_DIR = 'desc';
const SEARCH_DEBOUNCE_MS = 400;

const DATE_FORMAT = 'DD.MM.YYYY HH:mm';

// Invoice statuses. status_name is resolved server side for 2 and 3 only,
// the rest arrive as the raw int.
const STATUSES = [
    {value: 0, label: 'Pending', color: 'default'},
    {value: 1, label: 'Paid', color: 'green'},
    {value: 2, label: 'Recurring active', color: 'green'},
    {value: 3, label: 'Recurring cancelled', color: 'red'},
    {value: 4, label: 'Recurring failed', color: 'red'},
    {value: 5, label: 'Recurring finished', color: 'default'},
    {value: 7, label: 'Chargeback', color: 'red'},
    {value: 8, label: 'Not confirmed', color: 'default'},
];

const KNOWN_PAYSYS = ['stripe', 'paypal', 'free'];

const CancelRetentionDiscounts = () => {
    const {token} = useToken();
    const [searchParams, setSearchParams] = useSearchParams();
    const [discounts, setDiscounts] = useState([]);
    const [meta, setMeta] = useState({});
    const [loading, setLoading] = useState(false);

    // Search box is debounced, so it keeps its own state until it hits the URL.
    const [search, setSearch] = useState(searchParams.get('search') || '');
    const pushedSearch = useRef(searchParams.get('search') || '');

    const page = Number(searchParams.get('page')) || 1;
    const perPage = Number(searchParams.get('per_page')) || DEFAULT_PER_PAGE;
    const sortDir = searchParams.get('sort_dir') === 'asc' ? 'asc' : DEFAULT_SORT_DIR;
    const from = searchParams.get('from') || '';
    const to = searchParams.get('to') || '';
    const status = searchParams.get('status') || '';
    const paysysId = searchParams.get('paysys_id') || '';

    const updateParams = (changes) => {
        const params = new URLSearchParams(searchParams);

        Object.entries(changes).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                params.delete(key);
            } else {
                params.set(key, String(value));
            }
        });

        setSearchParams(params);
    };

    useEffect(() => {
        loadDiscounts();
    }, [searchParams]);

    // The URL changed on its own (back/forward, cleared filters) - follow it.
    useEffect(() => {
        const urlSearch = searchParams.get('search') || '';

        if (urlSearch !== pushedSearch.current) {
            pushedSearch.current = urlSearch;
            setSearch(urlSearch);
        }
    }, [searchParams]);

    useEffect(() => {
        if (search === pushedSearch.current) {
            return;
        }

        const timer = setTimeout(() => {
            pushedSearch.current = search;
            updateParams({search: search, page: undefined});
        }, SEARCH_DEBOUNCE_MS);

        return () => clearTimeout(timer);
    }, [search, searchParams]);

    const loadDiscounts = () => {
        setLoading(true);

        Api.get(BASE_URL, {
            page: page,
            per_page: perPage,
            sort_dir: sortDir,
            search: searchParams.get('search') || undefined,
            from: from || undefined,
            to: to || undefined,
            status: status !== '' ? status : undefined,
            paysys_id: paysysId || undefined,
        }).then(res => {
            setDiscounts(res.data.data);
            setMeta(res.data.meta || {});
        }).catch(() => {
            setDiscounts([]);
            setMeta({});
        }).finally(() => {
            setLoading(false);
        });
    };

    const handleRangeChange = (dates) => {
        if (!dates || !dates[0] || !dates[1]) {
            updateParams({from: undefined, to: undefined, page: undefined});

            return;
        }

        // `to` is compared as a string server side, so a bare date would cut off that day.
        updateParams({
            from: dates[0].startOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            to: dates[1].endOf('day').format('YYYY-MM-DDTHH:mm:ss'),
            page: undefined,
        });
    };

    const handleTableChange = (pagination, filters, sorter) => {
        const changes = {};

        if (pagination.pageSize !== perPage) {
            changes.per_page = pagination.pageSize;
            changes.page = undefined;
        } else {
            changes.page = pagination.current > 1 ? pagination.current : undefined;
        }

        if (sorter?.column) {
            const nextSortDir = sorter.order === 'ascend' ? 'asc' : 'desc';

            if (nextSortDir !== sortDir) {
                changes.sort_dir = nextSortDir === DEFAULT_SORT_DIR ? undefined : nextSortDir;
                changes.page = undefined;
            }
        }

        updateParams(changes);
    };

    const clearFilters = () => {
        setSearch('');
        setSearchParams({});
    };

    const hasFilters = !!(search || from || to || status !== '' || paysysId);

    const getStatusLabel = (record) => {
        const found = STATUSES.find(item => item.value === record.status);

        if (!found) {
            return <Tag>{record.status_name ?? record.status}</Tag>;
        }

        return <Tag color={found.color}>{found.label}</Tag>;
    };

    const formatDate = (value) => value ? dayjs(value).format(DATE_FORMAT) : '-';

    const formatMoney = (value, currency) => {
        if (value === null || value === undefined) {
            return '-';
        }

        return `${Number(value).toFixed(2)} ${currency || ''}`.trim();
    };

    // The API has no endpoint for the available payment systems, so offer the
    // known ones plus whatever shows up in the current result set.
    const paysysOptions = useMemo(() => {
        const values = new Set(KNOWN_PAYSYS);

        discounts.forEach(row => row.paysys_id && values.add(row.paysys_id));

        if (paysysId) {
            values.add(paysysId);
        }

        return [...values].map(value => ({label: value, value: value}));
    }, [discounts, paysysId]);

    const columns = [
        {
            title: 'User',
            dataIndex: 'email',
            key: 'email',
            render: (value, record) => (
                <>
                    <div>{[record.name_f, record.name_l].filter(Boolean).join(' ')}</div>
                    <Link to={'/user/' + record.user_id}>{value}</Link>
                </>
            ),
        },
        {
            title: 'Discount used',
            dataIndex: 'discount_used_at',
            key: 'discount_used_at',
            sorter: true,
            sortOrder: sortDir === 'asc' ? 'ascend' : 'descend',
            sortDirections: ['descend', 'ascend'],
            render: value => formatDate(value),
        },
        {
            title: 'Payment system',
            dataIndex: 'paysys_id',
            key: 'paysys_id',
            render: value => value ? <Tag>{value}</Tag> : '-',
        },
        {
            title: 'Status',
            dataIndex: 'status',
            key: 'status',
            render: (value, record) => getStatusLabel(record),
        },
        {
            title: 'Current price',
            dataIndex: 'second_total',
            key: 'second_total',
            render: (value, record) => (
                <Tag color="purple">{formatMoney(value, record.currency)}</Tag>
            ),
        },
        {
            title: 'Original total',
            dataIndex: 'first_total',
            key: 'first_total',
            render: (value, record) => formatMoney(value, record.currency),
        },
        {
            title: 'Subscription started',
            dataIndex: 'tm_added',
            key: 'tm_added',
            render: value => formatDate(value),
        },
        {
            title: 'Cancelled',
            dataIndex: 'tm_cancelled',
            key: 'tm_cancelled',
            render: value => value ? <Tag color="red">{formatDate(value)}</Tag> : '-',
        },
    ];

    return (
        <Content style={{margin: '0 16px'}}>
            <Breadcrumb style={{margin: '16px 0'}}>
                <Breadcrumb.Item>Fitlap</Breadcrumb.Item>
                <Breadcrumb.Item>Cancel retention discounts</Breadcrumb.Item>
            </Breadcrumb>

            <div className="site-layout-card">
                <Divider orientation="left">
                    <Title style={{margin: 0}} level={4}>
                        Cancel retention discounts ({meta.total || 0})
                    </Title>
                </Divider>

                <div style={{
                    marginBottom: 20,
                    padding: 16,
                    background: token.colorBgElevated,
                    borderRadius: 8,
                    border: `1px solid ${token.colorBorder}`
                }}>
                    <Row gutter={[16, 16]}>
                        <Space wrap style={{width: '100%'}}>
                            <Input
                                placeholder="Search by email"
                                value={search}
                                allowClear
                                onChange={(e) => setSearch(e.target.value)}
                                style={{width: 240}}
                            />

                            <RangePicker
                                value={[from ? dayjs(from) : null, to ? dayjs(to) : null]}
                                onChange={handleRangeChange}
                                format="DD.MM.YYYY"
                            />

                            <Select
                                placeholder="Status"
                                value={status !== '' ? Number(status) : undefined}
                                onChange={(value) => updateParams({
                                    status: value === undefined ? undefined : value,
                                    page: undefined
                                })}
                                options={STATUSES.map(item => ({label: item.label, value: item.value}))}
                                style={{width: 190}}
                                allowClear
                            />

                            <Select
                                placeholder="Payment system"
                                value={paysysId || undefined}
                                onChange={(value) => updateParams({paysys_id: value, page: undefined})}
                                options={paysysOptions}
                                style={{width: 170}}
                                allowClear
                            />

                            {hasFilters && (
                                <Button onClick={clearFilters}>
                                    Clear filters
                                </Button>
                            )}
                        </Space>
                    </Row>
                </div>

                <Table
                    rowKey="invoice_id"
                    size="small"
                    loading={loading}
                    columns={columns}
                    dataSource={discounts}
                    onChange={handleTableChange}
                    locale={{
                        emptyText: <Empty description="No one has used the cancel discount yet"/>
                    }}
                    pagination={{
                        current: Number(meta.current_page) || page,
                        pageSize: Number(meta.per_page) || perPage,
                        total: Number(meta.total) || 0,
                        showSizeChanger: true,
                        pageSizeOptions: ['10', '20', '50', '100'],
                    }}
                />
            </div>
        </Content>
    );
};

export default CancelRetentionDiscounts;
